import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Bell, Trash2, Search as SearchIcon, MailOpen, Heart, X, Ruler, RefreshCw, Pause, Play, Pencil, Mail } from "lucide-react";
import api from "../lib/apiClient";
import { Editable } from "../components/Editable";

export default function Alerts() {
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState([]);
  const [searches, setSearches] = useState([]);
  const [caps, setCaps] = useState(null);
  const [sizingReminders, setSizingReminders] = useState([]);
  const [catalogCount, setCatalogCount] = useState(null);

  const load = () => {
    api.get("/alerts").then(({ data }) => setAlerts(data)).catch(() => {});
    api.get("/saved-searches").then(({ data }) => setSearches(data)).catch(() => {});
    api.get("/email/capabilities").then(({ data }) => setCaps(data)).catch(() => {});
    api.get("/family/sizing-reminders").then(({ data }) => setSizingReminders(data || [])).catch(() => {});
    api.get("/collections").then(({ data }) => setCatalogCount((data || []).length)).catch(() => {});
  };
  const emailLive = !!caps?.saved_search_digest?.verified_live;
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const open = async (a) => {
    await api.post(`/alerts/${a.id}/open`).catch(() => {});
    setAlerts((prev) => prev.map((x) => (x.id === a.id ? { ...x, opened: true } : x)));
    if (a.kind === "new_look") { navigate(`/c/${a.collection_id}?look=${a.look_id}`); return; }
    navigate(`/deal/${a.collection_id || a.sale_id}`);
  };

  const love = async (e, a) => {
    e.stopPropagation();
    try {
      const { data } = await api.post(`/alerts/${a.id}/love`);
      toast.success(data.already ? "Already in your saved collections" : "Saved to your collections ❤");
    } catch {
      toast.error("Could not save this deal");
    }
  };

  const dismiss = async (e, a) => {
    e.stopPropagation();
    setAlerts((prev) => prev.filter((x) => x.id !== a.id));
    await api.delete(`/alerts/${a.id}`).catch(() => {});
  };

  const removeSearch = async (id) => {
    await api.delete(`/saved-searches/${id}`).catch(() => {});
    toast.success("Saved search removed");
    load();
  };

  const renameSearch = async (s) => {
    const name = window.prompt("Rename saved search", s.name);
    if (name == null || !name.trim()) return;
    await api.put(`/saved-searches/${s.id}`, { name: name.trim() }).catch(() => toast.error("Rename failed"));
    toast.success("Renamed");
    load();
  };

  const toggleSearch = async (s) => {
    await api.put(`/saved-searches/${s.id}`, { active: !s.active }).catch(() => {});
    toast.success(!s.active ? "Notifications resumed" : "Notifications paused");
    load();
  };

  // Per-search email opt-in (default OFF, never automatic). Daily digest, only new approved + in-stock matches, silent otherwise.
  const toggleEmail = async (s) => {
    try {
      const { data } = await api.put(`/saved-searches/${s.id}/email-alerts`, { enabled: !s.email_alerts });
      toast.success(data.email_alerts ? (data.alerts_live ? "Daily email digest on for this search" : "Email digest preference saved — nothing is sent until email digests launch (coming soon)") : "Email digest preference off for this search");
      load();
    } catch { toast.error("Couldn't update email setting"); }
  };

  // Price watch: only the target budget changes; family selection, filters, collection and selected accessories are kept verbatim.
  const changeTarget = async (s) => {
    const raw = window.prompt(`New target budget for ${s.filters.watch_collection_name || "this collection"} (whole selected family, excludes tax and shipping)`, String(s.filters.budget_amount || ""));
    if (raw == null) return;
    const amount = Number(String(raw).replace(/[^0-9.]/g, ""));
    if (!(amount > 0)) { toast.error("Enter a budget above $0."); return; }
    try {
      await api.put(`/saved-searches/${s.id}`, { filters: { ...s.filters, budget_amount: amount } });
      toast.success(`Target updated to $${amount.toFixed(0)}`);
      load();
    } catch { toast.error("Couldn't change the target"); }
  };

  const rerunSearch = async (s) => {
    if (s.kind === "match_builder" && !s.filters?.watch_collection_id) {
      // Open the finder pre-filled with these criteria and run it.
      navigate("/family-deals", { state: { rerunFilters: s.filters, runNow: true } });
      return;
    }
    await api.post(`/saved-searches/${s.id}/rerun`).catch(() => {});
    toast.success("Re-checked for new matches");
    load();
  };

  const restartSearch = async (s) => {
    await api.put(`/saved-searches/${s.id}`, { restart: true, watch_days: 30 }).catch(() => toast.error("Couldn't restart"));
    toast.success("Search restarted — watching 30 more days");
    load();
  };

  const extendSearch = async (s) => {
    await api.put(`/saved-searches/${s.id}`, { restart: true, watch_days: 30 }).catch(() => toast.error("Couldn't extend"));
    toast.success("Extended — watching 30 days from today");
    load();
  };

  const editSearch = (s) => {
    if (s.filters?.watch_collection_id) {
      changeTarget(s);
    } else if (s.kind === "match_builder") {
      navigate("/family-deals", { state: { rerunFilters: s.filters, runNow: false } });
    } else {
      renameSearch(s);
    }
  };

  return (
    <div className="space-y-8 fade-up">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading font-black text-3xl sm:text-4xl text-ink flex items-center gap-2"><Bell className="text-brand" /> <Editable id="alerts.title">Ding Me</Editable></h1>
          {catalogCount === 0
            ? <p className="text-ink-soft mt-1" data-testid="alerts-prelaunch-subtitle">Your searches are saved. Matches will appear here as our first collections become available.</p>
            : <Editable as="p" id="alerts.subtitle" className="text-ink-soft mt-1">Deals matching your saved searches. Choose how you're notified in Know Me.</Editable>}
          <p className="text-xs text-ink-muted mt-2" data-testid="ding-channel-note"><b>In-app:</b> available now · <b>Email digest:</b> {emailLive ? "available — switched on per saved search below, never automatically" : "coming soon — switched on per saved search below once it launches"} · <b>SMS:</b> coming soon</p>
        </div>
        <button onClick={() => navigate("/loved")} data-testid="go-loved-btn" className="h-11 px-5 rounded-full border-2 border-brand/30 text-brand bg-white font-bold text-sm flex items-center gap-2 hover:bg-brand-light transition-all">
          <Heart size={16} /> Saved collections
        </button>
      </div>

      {sizingReminders.length > 0 && (
        <section data-testid="ding-sizing-reminders">
          <h2 className="font-heading font-extrabold text-xl text-ink mb-3 flex items-center gap-2"><Ruler size={18} className="text-gold-text" /> Sizing check-ins ({sizingReminders.length})</h2>
          <div className="space-y-3">
            {sizingReminders.map((r) => (
              <div key={r.child_id} data-testid={`ding-sizing-${r.child_id}`} className="bg-gold/10 border border-gold/30 rounded-2xl p-4 flex items-center justify-between gap-3 flex-wrap">
                <p className="text-sm text-ink font-semibold">{r.message}</p>
                <button onClick={() => navigate("/family-profile")} data-testid={`ding-review-sizing-${r.child_id}`} className="h-9 px-4 rounded-full bg-gold text-white font-bold text-sm hover:bg-gold-hover transition-all shrink-0">Review sizing</button>
              </div>
            ))}
          </div>
        </section>
      )}

      <section>
        <h2 className="font-heading font-extrabold text-xl text-ink mb-3 flex items-center gap-2"><SearchIcon size={18} /> <Editable id="alerts.saved-title">Saved searches</Editable> ({searches.length})</h2>
        {searches.length === 0 ? (
          <div className="bg-white rounded-3xl border border-line p-6 text-ink-muted" data-testid="saved-searches-empty"><Editable as="p" id="alerts.saved-empty">No saved searches yet. Choose your children and filters in Curated Family Sets, then tap "Save search". Matches appear here in-app.</Editable> <span data-testid="email-capability-note">{emailLive ? "Each saved search can send you a daily email digest of new matches — text alerts are coming soon." : "Email digests and text alerts are coming soon."}</span></div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-3">
            {searches.map((s) => (
              <div key={s.id} data-testid={`saved-search-${s.id}`} className="bg-white rounded-2xl border border-line shadow-soft p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-bold text-ink truncate" data-testid={`saved-search-name-${s.id}`}>{s.name}</p>
                    <p className="text-xs text-ink-muted mt-0.5" data-testid={`saved-search-meta-${s.id}`}>
                      {s.filters?.watch_collection_id ? "Price watch" : s.kind === "match_builder" ? "Family set search" : "Deal search"} ·{" "}
                      {s.status === "expired"
                        ? <span className="text-brand font-semibold">expired</span>
                        : (s.indefinite || s.days_remaining == null)
                          ? <span className="text-ink-soft font-semibold">watching until you stop it</span>
                          : <>watching · {s.days_remaining} {s.days_remaining === 1 ? "day" : "days"} left{s.end_date_display ? ` · ends ${s.end_date_display}` : ""}</>}
                      {s.status === "expired" ? null : <>{" · "}{s.active ? <span className="text-success font-semibold">active</span> : <span className="text-ink-soft font-semibold">paused (window still counting down)</span>}</>}
                    </p>
                    {s.status !== "expired" && !s.indefinite && s.days_remaining != null && <p className="text-[11px] text-ink-muted mt-0.5" data-testid={`saved-search-duration-${s.id}`}>Each saved search watches for 30 days; Extend restarts the 30 days from today. Whether collections exist yet is separate from this window.</p>}
                    {s.filters?.watch_collection_id && <p className="text-xs text-ink-soft mt-1" data-testid={`watch-summary-${s.id}`}>Watching <b>{s.filters.watch_collection_name || s.filters.watch_collection_id}</b> · target ${Number(s.filters.budget_amount || 0).toFixed(0)}{s.filters.budget_type === "per_child" ? " per child" : ""} · {(s.filters.watch_child_ids || s.filters.child_ids || []).length} selected {((s.filters.watch_child_ids || s.filters.child_ids || []).length === 1) ? "child" : "children"}{(s.filters.watch_accessory_ids || []).length ? ` · ${s.filters.watch_accessory_ids.length} selected accessor${s.filters.watch_accessory_ids.length === 1 ? "y" : "ies"}` : ""} · in-app only. Dings only when the same complete total is within target and every other rule, stock and price check holds.</p>}
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-1.5 mt-3 pt-3 border-t border-line">
                  {s.status === "expired" ? (
                    <button onClick={() => restartSearch(s)} data-testid={`restart-search-${s.id}`} className="h-9 px-3 rounded-full bg-brand text-white font-bold text-xs flex items-center gap-1.5 hover:bg-brand-hover transition-all"><Play size={14} /> Restart</button>
                  ) : (
                    <button onClick={() => rerunSearch(s)} data-testid={`rerun-search-${s.id}`} className="h-9 px-3 rounded-full bg-brand text-white font-bold text-xs flex items-center gap-1.5 hover:bg-brand-hover transition-all"><RefreshCw size={14} /> Rerun</button>
                  )}
                  <button onClick={() => editSearch(s)} data-testid={`edit-search-${s.id}`} className="h-9 px-3 rounded-full border border-line text-ink-soft font-bold text-xs flex items-center gap-1.5 hover:bg-cream transition-all"><Pencil size={14} /> {s.filters?.watch_collection_id ? "Change target" : "Edit"}</button>
                  <button onClick={() => renameSearch(s)} data-testid={`rename-search-${s.id}`} className="h-9 px-3 rounded-full border border-line text-ink-soft font-bold text-xs hover:bg-cream transition-all">Rename</button>
                  {s.status !== "expired" && !s.indefinite && s.days_remaining != null && (
                    <button onClick={() => extendSearch(s)} data-testid={`extend-search-${s.id}`} title="Saved searches watch for 30 days; Extend restarts the 30 days from today." className="h-9 px-3 rounded-full border border-line text-ink-soft font-bold text-xs hover:bg-cream transition-all">Extend</button>
                  )}
                  {s.status !== "expired" && (
                    <><button onClick={() => toggleSearch(s)} data-testid={`toggle-search-${s.id}`} className="h-9 px-3 rounded-full border border-line text-ink-soft font-bold text-xs flex items-center gap-1.5 hover:bg-cream transition-all">{s.active ? <><Pause size={14} /> Pause</> : <><Play size={14} /> Resume</>}</button>
                    <button onClick={() => toggleEmail(s)} data-testid={`toggle-email-${s.id}`} aria-pressed={!!s.email_alerts} className={`h-9 px-3 rounded-full border font-bold text-xs flex items-center gap-1.5 transition-all ${s.email_alerts ? "border-brand bg-brand/10 text-brand" : "border-line text-ink-soft hover:bg-cream"}`}><Mail size={14} /> {s.email_alerts ? "Email digest: on" : "Email digest: off"}{emailLive ? "" : " · coming soon"}</button></>
                  )}
                  <button onClick={() => removeSearch(s.id)} data-testid={`delete-search-${s.id}`} className="h-9 w-9 flex items-center justify-center rounded-full text-brand hover:bg-brand-light transition-all ml-auto" title="Delete"><Trash2 size={16} /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <section>
        <h2 className="font-heading font-extrabold text-xl text-ink mb-3 flex items-center gap-2"><Bell size={18} /> <Editable id="alerts.matches-title">Matching deal alerts</Editable> ({alerts.length})</h2>
        {alerts.length === 0 ? (
          <Editable as="div" id="alerts.matches-empty" className="bg-white rounded-3xl border border-line p-6 text-ink-muted">No alerts yet. When a sale matches a saved search, it appears here.</Editable>
        ) : (
          <div className="space-y-3">
            {alerts.map((a) => (
              <div
                key={a.id}
                data-testid={`alert-${a.id}`}
                onClick={() => open(a)}
                className={`w-full text-left rounded-2xl border p-4 flex items-center justify-between gap-3 transition-all hover:shadow-card cursor-pointer ${a.opened ? "bg-white border-line" : "bg-brand-light border-brand/30"}`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${a.opened ? "bg-cream text-ink-muted" : "bg-brand text-white"}`}>
                    {a.opened ? <MailOpen size={18} /> : <Bell size={18} />}
                  </div>
                  <div className="min-w-0">
                    {a.kind === "new_look" ? (<>
                      <p className="font-bold text-ink truncate" data-testid={`alert-look-title-${a.id}`}>New approved look from {a.contributor_name}: “{a.look_name}”</p>
                      <p className="text-sm text-ink-soft truncate">{a.collection_name}{a.is_demo ? " · demo collection" : ""} · {a.match_status === "matched" ? `Matches “${a.search_name}”${a.matched_children?.length ? ` for ${a.matched_children.join(", ")}` : ""}` : "From a contributor you follow · in-app only"}</p>
                    </>) : a.kind === "price_watch" ? (<>
                      <p className="font-bold text-ink truncate" data-testid={`alert-watch-title-${a.id}`}>{a.sale_title} is now within your ${Number(a.budget_limit ?? a.budget_amount).toFixed(0)} budget: ${Number(a.watched_total).toFixed(2)}</p>
                      <p className="text-sm text-ink-soft truncate">Selected family{a.matched_children?.length ? ` · ${a.matched_children.join(", ")}` : ""} · every other rule still holds · excludes tax and shipping</p>
                    </>) : (<>
                    <p className="font-bold text-ink truncate">
                      A matching {a.occasion || "new"} set just dropped{(a.matched_children && a.matched_children.length) ? ` in ${a.matched_children.join(" & ")}'s sizes` : " in your children's sizes"}.
                    </p>
                    <p className="text-sm text-ink-soft truncate">
                      {a.occasion ? `${a.occasion} · ` : ""}{(a.matched_children && a.matched_children.length) ? `Matches ${a.matched_children.join(", ")}` : "Tap to see the coordinated look"}
                    </p>
                    </>)}
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {!a.opened && <span className="text-xs font-bold text-brand mr-1">NEW</span>}
                  {a.kind !== "new_look" && <button onClick={(e) => love(e, a)} data-testid={`alert-love-${a.id}`} title="Save to your collections" className="p-2 rounded-full text-brand hover:bg-white/70 transition-all"><Heart size={18} /></button>}
                  <button onClick={(e) => dismiss(e, a)} data-testid={`alert-dismiss-${a.id}`} title="Hide this notification" className="p-2 rounded-full text-ink-muted hover:bg-white/70 hover:text-ink transition-all"><X size={18} /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
